import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  UserCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';
import {
  SERVICE_SCOPE,
  filterUnmatchedByScope,
  formatServiceSlotLabel,
  getNextServiceSlot,
  isUnmatchedBooking,
  rideCompatibleWithBooking,
} from '../../utils/adminBookingFilters';

const getRide = (booking) => booking.ride || booking.Ride || null;

const getName = (person) => {
  if (!person) return 'Driver not listed';
  return `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.email || 'Name not provided';
};

const getRiderName = (booking) => {
  if (!booking.rider) return 'Rider not listed';
  return `${booking.rider.firstName || ''} ${booking.rider.lastName || ''}`.trim() ||
    booking.rider.email ||
    'Rider not listed';
};

const getPickup = (booking) => {
  if (booking.pickupStreet || booking.pickupCity || booking.pickupPostcode) {
    return [
      booking.pickupStreet,
      [booking.pickupCity, booking.pickupPostcode].filter(Boolean).join(' '),
    ].filter(Boolean).join(', ');
  }
  return booking.pickupLocation || 'Pickup not provided';
};

const formatDate = (value) => {
  if (!value) return 'Date not specified';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return 'Date not specified';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

function ManualMatching() {
  const [bookings, setBookings] = useState([]);
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [serviceScope, setServiceScope] = useState(SERVICE_SCOPE.NEXT_SERVICE);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [assigningRideId, setAssigningRideId] = useState(null);
  const [confirmMismatch, setConfirmMismatch] = useState(null);

  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [bookingsResponse, ridesResponse] = await Promise.all([
        axiosInstance.get(API_URLS.admin.bookings),
        axiosInstance.get(API_URLS.admin.rides),
      ]);

      if (!Array.isArray(bookingsResponse.data) || !Array.isArray(ridesResponse.data)) {
        throw new Error('The server returned invalid booking or ride data.');
      }

      setBookings(bookingsResponse.data);
      setRides(ridesResponse.data);
    } catch (err) {
      console.error('Error loading manual matching overview:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load requests and rides.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const unmatchedBookings = useMemo(
    () => bookings.filter(isUnmatchedBooking),
    [bookings]
  );

  const nextServiceSlot = useMemo(
    () => getNextServiceSlot(unmatchedBookings),
    [unmatchedBookings]
  );

  const scopedUnmatched = useMemo(
    () => filterUnmatchedByScope(unmatchedBookings, serviceScope, nextServiceSlot),
    [unmatchedBookings, serviceScope, nextServiceSlot]
  );

  const selectedBooking = useMemo(
    () => scopedUnmatched.find((b) => b.id === selectedBookingId) || null,
    [scopedUnmatched, selectedBookingId]
  );

  useEffect(() => {
    if (selectedBookingId && !scopedUnmatched.some((b) => b.id === selectedBookingId)) {
      setSelectedBookingId(null);
    }
  }, [scopedUnmatched, selectedBookingId]);

  const availableRides = useMemo(() => rides.filter((ride) => (
    String(ride.status || '').toLowerCase() === 'scheduled' &&
    Number(ride.availableSeats) > 0
  )), [rides]);

  const ridesForSelection = useMemo(() => {
    if (!selectedBooking) return availableRides;
    const compatible = availableRides.filter((ride) => rideCompatibleWithBooking(ride, selectedBooking));
    return compatible.length > 0 ? compatible : availableRides;
  }, [availableRides, selectedBooking]);

  const performAssign = async (bookingId, rideId, allowScheduleMismatch = false) => {
    setAssigningRideId(rideId);
    setError('');
    setSuccess('');
    try {
      await axiosInstance.post(API_URLS.admin.assignBookingToRide(bookingId), {
        rideId,
        allowScheduleMismatch,
      });
      setSuccess('Booking assigned. Rider and driver are notified by email; SMS is sent when phone numbers are on file and SMS alerts are enabled.');
      setSelectedBookingId(null);
      setConfirmMismatch(null);
      await refreshData();
    } catch (err) {
      console.error('Assign failed:', err);
      setError(err.response?.data?.message || err.message || 'Failed to assign booking.');
    } finally {
      setAssigningRideId(null);
    }
  };

  const handleAssignClick = (ride) => {
    if (!selectedBooking) return;

    const compatible = rideCompatibleWithBooking(ride, selectedBooking);
    if (!compatible) {
      setConfirmMismatch({ ride });
      return;
    }
    performAssign(selectedBooking.id, ride.id, false);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manual Matching</h1>
          <p className="mt-2 text-sm text-gray-700">
            Select an unmatched request, then assign it to a ride with open seats.
          </p>
        </div>
        <button
          type="button"
          onClick={refreshData}
          disabled={loading}
          className="inline-flex items-center self-start rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowPathIcon className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div role="alert" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} className="font-medium underline">
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div role="status" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          <span className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
            {success}
          </span>
          <button type="button" onClick={() => setSuccess('')} className="font-medium underline">
            Dismiss
          </button>
        </div>
      )}

      <div className="mb-6 rounded-lg bg-white p-4 shadow">
        <label htmlFor="manual-service-scope" className="block text-sm font-medium text-gray-700">
          Unmatched list
        </label>
        <select
          id="manual-service-scope"
          value={serviceScope}
          onChange={(event) => setServiceScope(event.target.value)}
          className="mt-1 block w-full max-w-xs rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value={SERVICE_SCOPE.NEXT_SERVICE}>Next service only</option>
          <option value={SERVICE_SCOPE.UPCOMING}>All upcoming</option>
          <option value={SERVICE_SCOPE.ALL}>All (incl. past)</option>
        </select>
        {serviceScope === SERVICE_SCOPE.NEXT_SERVICE && nextServiceSlot && (
          <p className="mt-2 text-sm text-gray-600">
            Next service: <span className="font-medium text-gray-900">{formatServiceSlotLabel(nextServiceSlot)}</span>
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex h-56 items-center justify-center rounded-lg bg-white shadow">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" aria-label="Loading requests and rides" />
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <section className="overflow-hidden rounded-lg bg-white shadow">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Unmatched requests <span className="text-gray-500">({scopedUnmatched.length})</span>
              </h2>
              <p className="mt-1 text-sm text-gray-500">Click a request to select it for assignment.</p>
            </div>
            {scopedUnmatched.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-gray-500">There are no unmatched requests in this view.</p>
            ) : (
              <ul className="max-h-[620px] divide-y divide-gray-200 overflow-y-auto">
                {scopedUnmatched.map((booking) => {
                  const isSelected = selectedBookingId === booking.id;
                  return (
                    <li key={booking.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedBookingId(booking.id)}
                        className={`w-full p-5 text-left transition-colors ${isSelected ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-500' : 'hover:bg-gray-50'}`}
                      >
                        <div className="flex items-start gap-3">
                          <UserCircleIcon className={`h-9 w-9 flex-shrink-0 ${isSelected ? 'text-indigo-500' : 'text-gray-400'}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <h3 className="font-medium text-gray-900">{getRiderName(booking)}</h3>
                              <span className="text-xs text-gray-500">Booking #{booking.id}</span>
                            </div>
                            <p className="mt-2 flex items-start gap-2 text-sm text-gray-600">
                              <MapPinIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                              <span>{getPickup(booking)}</span>
                            </p>
                            <p className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                              <CalendarIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                              {formatDate(booking.date)}
                              {(booking.serviceSession || booking.serviceTime) && ` · ${booking.serviceSession || ''}${booking.serviceTime ? ` ${booking.serviceTime}` : ''}`}
                            </p>
                            <p className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                              <UserGroupIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                              {booking.seats || booking.passengers || 1} passenger(s)
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="overflow-hidden rounded-lg bg-white shadow">
            <div className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Available rides <span className="text-gray-500">({ridesForSelection.length})</span>
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {selectedBooking
                  ? 'Rides matching date/session shown first when possible.'
                  : 'Select an unmatched request to enable Assign.'}
              </p>
            </div>
            {ridesForSelection.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-gray-500">There are no scheduled rides with seats available.</p>
            ) : (
              <ul className="max-h-[620px] divide-y divide-gray-200 overflow-y-auto">
                {ridesForSelection.map((ride) => {
                  const compatible = selectedBooking && rideCompatibleWithBooking(ride, selectedBooking);
                  const isAssigning = assigningRideId === ride.id;
                  return (
                    <li key={ride.id} className="p-5">
                      <div className="flex items-start gap-3">
                        <UserCircleIcon className="h-9 w-9 flex-shrink-0 text-gray-400" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <h3 className="font-medium text-gray-900">{getName(ride.driver)}</h3>
                            <span className="text-xs text-gray-500">Ride #{ride.id}</span>
                          </div>
                          <p className="mt-2 flex items-start gap-2 text-sm text-gray-600">
                            <MapPinIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                            <span>{ride.pickupLocation || 'Pickup not provided'} <span className="text-gray-400">to</span> {ride.dropoffLocation || 'Destination not provided'}</span>
                          </p>
                          <p className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                            <CalendarIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            {formatDate(ride.date)}
                            {ride.serviceSession && ` · ${ride.serviceSession}`}
                          </p>
                          <p className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                            <ClockIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            {ride.time || 'Time not specified'}
                          </p>
                          <p className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                            <UserGroupIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            {ride.availableSeats} seat(s) available
                          </p>
                          <button
                            type="button"
                            disabled={!selectedBooking || isAssigning}
                            onClick={() => handleAssignClick(ride)}
                            className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isAssigning ? 'Assigning…' : compatible === false ? 'Assign anyway…' : 'Assign to this ride'}
                          </button>
                          {selectedBooking && compatible === false && (
                            <p className="mt-1 text-xs text-amber-700">Date or service session differs from selected booking.</p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      )}

      {confirmMismatch && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4" role="dialog" aria-modal="true" aria-labelledby="mismatch-dialog-title">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 id="mismatch-dialog-title" className="text-lg font-semibold text-gray-900">
              Schedule mismatch
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              This ride does not match the booking date or service session. Assign only if you are sure transport has coordinated offline.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmMismatch(null)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={assigningRideId != null}
                onClick={() => performAssign(selectedBooking.id, confirmMismatch.ride.id, true)}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                Assign anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManualMatching;
